import { apiFetch } from '../lib/api';

export type ConnectionState = 'connected' | 'error' | 'unknown' | 'stale' | 'missing' | 'not_provided';

export interface InventoryCandidate {
  path: string;
  size: number;
  modifyTime: number;
  matched_filename_pattern: boolean;
}

export interface InventoryScanDiagnostic {
  kind: 'wp_inventory_scan';
  scanned_at: string;
  searched_dirs: string[];
  fallback_dirs: string[];
  candidates_seen: InventoryCandidate[];
  matched_candidates: InventoryCandidate[];
  newest_file_seen: InventoryCandidate | null;
  filename_pattern: string;
  ext_pattern: string;
  error: string | null;
  // Added by vendor-pull.ts after the scanner returns:
  last_successful_pull_at?: string | null;
  last_successful_s3_key?: string | null;
  job_id?: number;
  scanned_at_db?: string;
  ingest_stats?: unknown;
  dedup_skipped?: boolean;
}

export interface FeedDetail {
  label: string;
  source: string;
  connection_state: ConnectionState;
  last_pull_at: string | null;
  rows_processed: number | null;
  provides: string[];
  help?: string | null;
  last_filename?: string | null;
  updated_sku_count?: number | null;
  missing_sku_count?: number | null;
  last_job_status?: string | null;
  next_scheduled_pull_at?: string | null;
  last_scan_at?: string | null;
  last_file_received_at?: string | null;
  last_scan_diagnostic?: InventoryScanDiagnostic | null;
}

export interface VendorConnections {
  wheelpros: {
    connected: boolean;
    connection_state: ConnectionState;
    last_pull_at: string | null;
    last_success: string | null;
    feeds: {
      catalog: FeedDetail;
      inventory: FeedDetail;
    };
  };
  atd: {
    connected: boolean;
    connection_state: ConnectionState;
    last_pull_at: string | null;
    last_success: string | null;
    feeds: {
      combined: FeedDetail;
    };
  };
  vision: {
    connected: boolean;
    connection_state: ConnectionState;
    last_pull_at: string | null;
    last_success: string | null;
    feeds: {
      msrp: FeedDetail;
      inv_update: FeedDetail;
    };
  };
}

export interface FeedRunMetrics {
  last_pull_at: string | null;
  rows_processed: number | null;
  new_count?: number | null;
  update_count?: number | null;
  status: string | null;
}

export interface FeedStatus {
  wheelpros: {
    last_pull_at: string | null;
    rows_processed: number | null;
    new_count: number | null;
    update_count: number | null;
    status: string | null;
    feeds: {
      catalog: FeedRunMetrics;
      inventory: FeedRunMetrics;
    };
  };
  atd: {
    last_pull_at: string | null;
    rows_processed: number | null;
    new_count: number | null;
    update_count: number | null;
    status: string | null;
    feeds: {
      combined: FeedRunMetrics;
    };
  };
}

export function getVendorConnections(): Promise<VendorConnections> {
  return apiFetch('/admin/vendor-connections');
}

export function getFeedStatus(): Promise<FeedStatus> {
  return apiFetch('/admin/feeds/status');
}

// On-demand SFTP probe — does NOT download files, does NOT write DB. Returns
// the same diagnostic shape that scheduled pulls persist into metadata.
export function probeWheelProsSftp(): Promise<InventoryScanDiagnostic> {
  return apiFetch('/admin/wp-inventory/probe-sftp', { method: 'POST' });
}
// ============================================================================
// APPEND to: admin-ui/src/api/feeds.ts
// ============================================================================

export interface VendorDryRunSummary {
  // Common
  total_rows?: number;
  in_stock?: number;
  truly_empty?: number;
  is_clearance?: number;
  is_request_quote?: number;
  // Rohana
  pricing_book_rows?: number;
  inventory_book_rows?: number;
  matched_rows?: number;
  // RC
  total_rows_rc?: number;
  in_stock_tn_or_nv?: number;
  no_map?: number;
  // Torque-specific
  sale_price_column_present?: boolean;
}

export interface VendorDryRunDigestEntry {
  vendor: string;
  available: boolean;
  date?: string | null;
  mode?: string | null;
  s3Key?: string;
  summary?: VendorDryRunSummary;
}

export interface VendorDryRunDigest {
  asOf: string;
  vendors: {
    rohana?: VendorDryRunDigestEntry;
    torque?: VendorDryRunDigestEntry;
    roughcountry?: VendorDryRunDigestEntry;
    // 2026-07-01: TireWeb SOAP vendors added — each puller now emits
    // dry-run-diff.json alongside the DB write. AXE stays in the map
    // to render an explicit "Missing" state until source is provided.
    kg1?: VendorDryRunDigestEntry;
    method?: VendorDryRunDigestEntry;
    wd?: VendorDryRunDigestEntry;
    axe?: VendorDryRunDigestEntry;
  };
}

export function getVendorDryRunDigest(): Promise<VendorDryRunDigest> {
  return apiFetch('/admin/vendor-dry-run/digest');
}
